import type { Session } from '@supabase/supabase-js';
import { dedupeDuplicateStocksRows } from '../../../../shared/localDb';
import type { InventoryDashboardRow, ProductBranchStockRow, StockRow, StockSort, StockStatusFilter } from '../../domain/stockTypes';
import { supabase } from '../../../../shared/supabase/supabaseClient';
import { useAuthStore } from '../../../../auth/store/useAuthStore';
import {
  buildNormalizedSqlExpression,
  normalizeSearchInput,
  tokenizeSearchInput,
} from '../../../../catalog/infra/productSqlSearchNormalize';
import {
  productMatchesCategoryFilter,
  sqlCategoryComparableValuesForFilter,
} from '../../../../catalog/domain-api';
import type { RemoteSnapshotStockRow } from '../sync/remoteSnapshotStockApply';
import { aggregateRemoteStocksByProduct } from '../../../../catalog/infra/productCatalogRemoteSnapshot';
import { getCatalogRamSnapshotCache } from '../../../../catalog/infra/catalogRamSnapshotCache';
import { normalizeStockUnitOfMeasure } from '../../../application/inventoryUnitNormalize';
import { getActiveCatalogSnapshotSync } from '../../../../catalog/infra/catalogSnapshotSync';
import { preflightCatalogSnapshotForRead } from '../../../../catalog/infra/catalogReadPreflight';
import { getComboParentProductIds } from '../../../../catalog/infra/comboParentProductIds';
import { getStockLotExpirySplit } from '../guards/fefoAllocator';
import { SyncPullFailedError } from '../../../../sync/core/syncPullErrors';
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

// Cache simple con debounce para dedupeDuplicateStocksRows
let lastDedupeTime = 0;
const DEDUPE_DEBOUNCE_MS = 5000;

// Función reutilizable para aplicar todos los filtros en una sola operación
const applyAllFilters = (
  row: StockRow,
  opts: GetStockByBranchOptions,
  productsById: Map<string, RemoteSnapshotProductRow>
): boolean => {
  const pinned = opts?.pinnedProductId != null && String(opts?.pinnedProductId).trim().length > 0;
  const searchTokens = tokenizeSearchInput(opts?.search ?? '');

  // Semántica intencional (P2 audit UX stock v2): el pin es un foco exclusivo —
  // muestra la fila del producto aunque no matchee el scope activo
  // (status/categoría/marca/proveedor/allowlist). La UI lo comunica con el chip
  // "Viendo solo" y los filtros seteados se reanudan al quitar el pin
  // (aplicar un filtro también limpia el pin — ver useInventoryStockListFlow).
  // Nota: la rama SQL legacy (buildStockByBranchSql) sí combina pin + filtros;
  // la rama RAM es la que corre en producción (ADR-017).
  if (pinned) return row.productId === String(opts?.pinnedProductId).trim();

  const matchesScope =
    stockRowMatchesStatusFilter(row, opts?.statusFilter) &&
    applyRemoteCategoryFilter(row, productsById, opts?.categoryId ?? undefined) &&
    applyRemoteBrandSupplierFilter(row, productsById, opts?.brandId, opts?.supplierId);
  if (!matchesScope) return false;

  if (opts?.productIdAllowlist != null) {
    if (opts.productIdAllowlist.length === 0) return false;
    if (!opts.productIdAllowlist.includes(row.productId)) return false;
  }

  if (searchTokens.length === 0) return true;

  const haystack = normalizeSearchInput(
    `${row.productName} ${row.productSku ?? ''} ${row.productBarcode ?? ''} ${row.variantLabel ?? ''} ${row.variantSku ?? ''} ${row.variantBarcode ?? ''}`
  );
  return searchTokens.every((token) => haystack.includes(token));
};

export type GetStockByBranchOptions = {
  limit?: number;
  offset?: number;
  search?: string;
  statusFilter?: StockStatusFilter;
  /** Como Catálogo: filtra por ID de categoría producto físico */
  categoryId?: string | null | 'all';
  /** Un solo producto (lista con chip “×” como en Informes › Ventas); ignora texto de búsqueda. */
  pinnedProductId?: string | null;
  /** brand_id (UUID) de la tabla brands. */
  brandId?: string | null;
  /** Proveedor preferido (`suppliers.id`). */
  supplierId?: string | null;
  /** Ordenamiento del lado de SQLite. Si se omite, ordena por cantidad ascendente. */
  sort?: StockSort | null;
  /**
   * #801: Incluir filas de variante además de las filas parent agregadas.
   * Por defecto false (backward-compatible: solo filas parent).
   * Cuando es true, devuelve parent rows + variant rows (con variantId/variantLabel/variantSku/variantBarcode).
   */
  includeVariants?: boolean;
  /**
   * Sesión Supabase para fallback RPC cuando SQLite no está disponible.
   * Si se omite, se usa la sesión del store de auth.
   */
  authSessionForRemoteFallback?: Session | null;
  /**
   * Restrict to these product IDs (e.g. no_sales alert allowlist from RPC).
   * Empty array → no rows. Undefined → no allowlist filter.
   */
  productIdAllowlist?: string[] | null;
  /**
   * Incluir filas sintéticas (qty 0) para productos listables sin fila de
   * stock en la sucursal — «catálogo ∪ movimientos». Default true (la lista
   * y el count quieren el catálogo completo). Pasar false en paths que
   * operan sobre filas de stock reales (conteo físico usa stockId para
   * decidir adjust vs receive; transferir stock que no existe no tiene
   * sentido).
   */
  includeUnstocked?: boolean;
};

const getStockStatusSqlClause = (
  statusFilter: StockStatusFilter | undefined
): { clause: string; params: string[] } | null => {
  if (!statusFilter || statusFilter === 'all') return null;

  const nowIso = new Date().toISOString();
  const expiringIso = new Date(Date.now() + SEVEN_DAYS_MS).toISOString();

  if (statusFilter === 'expired') {
    return {
      clause: 'agg.expires_at IS NOT NULL AND agg.expires_at < ?',
      params: [nowIso],
    };
  }
  if (statusFilter === 'negative') {
    return {
      clause: '(agg.expires_at IS NULL OR agg.expires_at >= ?) AND agg.quantity < 0',
      params: [nowIso],
    };
  }
  if (statusFilter === 'low') {
    // «Stock bajo» excluye los que vencen pronto: esos ya cuentan en
    // 'expiring' (precedencia EXPIRING > LOW, igual que el badge de la fila).
    return {
      clause:
        '(agg.expires_at IS NULL OR agg.expires_at >= ?) AND agg.quantity > 0 AND agg.quantity <= agg.min_quantity',
      params: [expiringIso],
    };
  }
  if (statusFilter === 'zero') {
    return {
      clause: '(agg.expires_at IS NULL OR agg.expires_at >= ?) AND agg.quantity = 0',
      params: [nowIso],
    };
  }
  // no_sales: qty>0; allowlist applied in applyAllFilters
  if (statusFilter === 'no_sales') {
    return {
      clause: '(agg.expires_at IS NULL OR agg.expires_at >= ?) AND agg.quantity > 0',
      params: [nowIso],
    };
  }

  // 'expiring': sin condición de mínimo — el vencimiento aplica igual aunque
  // el producto esté bajo mínimo (antes quedaba invisible al alerta).
  return {
    clause:
      'agg.expires_at IS NOT NULL AND agg.expires_at >= ? AND agg.expires_at < ? AND agg.quantity > 0',
    params: [nowIso, expiringIso],
  };
};

const stockRowMatchesStatusFilter = (row: StockRow, statusFilter: StockStatusFilter | undefined) => {
  if (!statusFilter || statusFilter === 'all') return true;

  const now = Date.now();
  const expiresTime = row.expiresAt ? new Date(row.expiresAt).getTime() : null;
  // #1497: lotes son la fuente de verdad del vencimiento — el agregado
  // expires_at puede venir stale hasta el próximo refresh del servidor.
  const lotsExpired =
    row.variantId == null &&
    (getStockLotExpirySplit(row.productId, row.branchId)?.expiredQuantity ?? 0) > 0;
  const isExpired = (expiresTime != null && expiresTime < now) || lotsExpired;
  const isExpiring = expiresTime != null && expiresTime >= now && expiresTime - now < SEVEN_DAYS_MS;

  if (statusFilter === 'expired') return isExpired;
  if (statusFilter === 'negative') return !isExpired && row.quantity < 0;
  if (statusFilter === 'zero') return !isExpired && row.quantity === 0;
  // 'low' excluye a los que vencen pronto: entran en 'expiring' (KPI == filtro).
  if (statusFilter === 'low')
    return !isExpired && !isExpiring && row.quantity > 0 && row.quantity <= row.minQuantity;
  if (statusFilter === 'no_sales') return !isExpired && row.quantity > 0;
  // 'expiring': sin cláusula de mínimo — un producto por vencer Y bajo mínimo
  // sigue siendo urgencia de vencimiento (antes quedaba fuera del alerta).
  return !isExpired && row.quantity > 0 && isExpiring;
};

const buildStockOrderByClause = (sort: StockSort | null | undefined): string => {
  const dir = sort?.endsWith('_desc') ? 'DESC' : 'ASC';
  switch (sort) {
    case 'name_asc':
    case 'name_desc':
      return `ORDER BY products.name COLLATE NOCASE ${dir}`;
    case 'price_asc':
    case 'price_desc':
      return `ORDER BY products.price ${dir}`;
    case 'stock_asc':
    case 'stock_desc':
      return `ORDER BY agg.quantity ${dir}`;
    case 'cost_asc':
    case 'cost_desc':
      return `ORDER BY agg.avg_cost ${dir}`;
    case 'value_asc':
    case 'value_desc':
      return `ORDER BY (agg.quantity * agg.avg_cost) ${dir}`;
    case 'expiry_asc':
    case 'expiry_desc':
      // «Sin vencimiento» siempre al final, en ambas direcciones.
      return `ORDER BY (agg.expires_at IS NULL) ASC, agg.expires_at ${dir}`;
    case 'variant_label_asc':
    case 'variant_label_desc':
      // SQLite path no tiene variantes; caer a nombre como fallback.
      return `ORDER BY products.name COLLATE NOCASE ${dir}`;
    default:
      return 'ORDER BY agg.quantity ASC';
  }
};

const stockRowComparator = (sort: StockSort | null | undefined): ((a: StockRow, b: StockRow) => number) => {
  const dir = sort?.endsWith('_desc') ? -1 : 1;
  switch (sort) {
    case 'name_asc':
    case 'name_desc':
      return (a, b) => a.productName.localeCompare(b.productName) * dir;
    case 'price_asc':
    case 'price_desc':
      return (a, b) => (Number(a.productPrice ?? 0) - Number(b.productPrice ?? 0)) * dir;
    case 'stock_asc':
    case 'stock_desc':
      return (a, b) => (a.quantity - b.quantity) * dir;
    case 'cost_asc':
    case 'cost_desc':
      return (a, b) => (a.avgCost - b.avgCost) * dir;
    case 'value_asc':
    case 'value_desc':
      return (a, b) => (a.quantity * a.avgCost - b.quantity * b.avgCost) * dir;
    case 'expiry_asc':
    case 'expiry_desc':
      // «Sin vencimiento» al final en ambas direcciones (antes el fallback
      // '9999' los ponía primero en expiry_desc).
      return (a, b) => {
        if (a.expiresAt == null && b.expiresAt == null) return 0;
        if (a.expiresAt == null) return 1;
        if (b.expiresAt == null) return -1;
        return a.expiresAt.localeCompare(b.expiresAt) * dir;
      };
    case 'variant_label_asc':
    case 'variant_label_desc':
      // #801: Sort by variant label; parent rows (null label) sort first.
      return (a, b) => {
        const al = a.variantLabel ?? '';
        const bl = b.variantLabel ?? '';
        return al.localeCompare(bl, 'es', { sensitivity: 'base' }) * dir;
      };
    default:
      return (a, b) => a.quantity - b.quantity;
  }
};

const buildStockByBranchSql = (
  branchId: string,
  opts: GetStockByBranchOptions | undefined,
  selectSql: string,
  includeOrderBy: boolean
) => {
  const pinned = opts?.pinnedProductId != null && String(opts.pinnedProductId).trim().length > 0;
  const searchTokens = tokenizeSearchInput(opts?.search ?? '');
  const whereClauses: string[] = [];
  const params: Array<string | number> = [branchId];

  if (!pinned) {
    for (const token of searchTokens) {
      whereClauses.push(
        `( ${['products.name', 'products.sku', "COALESCE(products.barcode, products.sku, '')"]
          .map((field) => `${buildNormalizedSqlExpression(field)} LIKE ?`)
          .join(' OR ')} )`
      );
      params.push('%' + token + '%', '%' + token + '%', '%' + token + '%');
    }
  }

  const statusClause = getStockStatusSqlClause(opts?.statusFilter);
  if (statusClause) {
    whereClauses.push(`(${statusClause.clause})`);
    params.push(...statusClause.params);
  }

  const categoryIdVal = opts?.categoryId;
  if (categoryIdVal && categoryIdVal !== 'all') {
    whereClauses.push('products.category_id = ?');
    params.push(categoryIdVal);
  }

  const brandIdVal =
    opts?.brandId != null && String(opts.brandId).trim() !== '' ? String(opts.brandId).trim() : '';
  if (brandIdVal) {
    whereClauses.push(`coalesce(products.brand_id, '') = ?`);
    params.push(brandIdVal);
  }

  const supplierTrim =
    opts?.supplierId != null && String(opts.supplierId).trim() !== ''
      ? String(opts.supplierId).trim()
      : '';
  if (supplierTrim) {
    whereClauses.push(`coalesce(products.preferred_supplier_id, '') = ?`);
    params.push(supplierTrim);
  }

  if (pinned) {
    whereClauses.push('agg.product_id = ?');
    params.push(String(opts?.pinnedProductId).trim());
  }

  const whereSql = whereClauses.length > 0 ? `WHERE ${whereClauses.join(' AND ')}` : '';
  const orderSql = includeOrderBy ? buildStockOrderByClause(opts?.sort) : '';

  return {
    sql: `${selectSql}
       FROM (
         SELECT
           MIN(s.id) AS id,
           s.product_id,
           s.branch_id,
           SUM(s.quantity) AS quantity,
           CASE
             WHEN SUM(s.quantity) > 0 THEN SUM(s.quantity * IFNULL(s.avg_cost, 0)) / SUM(s.quantity)
             ELSE 0
           END AS avg_cost,
           MAX(s.min_quantity) AS min_quantity,
           MIN(s.expires_at) AS expires_at
         FROM stocks s
         WHERE s.branch_id = ? AND s.product_id IS NOT NULL AND s.variant_id IS NULL
         GROUP BY s.product_id, s.branch_id
       ) agg
       JOIN products ON products.id = agg.product_id
       AND COALESCE(products.is_misc, 0) = 0
       AND (COALESCE(products.item_type, 'physical') = 'physical')
       LEFT JOIN brands ON brands.id = products.brand_id
       LEFT JOIN suppliers ON suppliers.id = products.preferred_supplier_id
       ${whereSql}
       ${orderSql}`,
    params,
  };
};

type RemoteSnapshotProductRow = {
  id: string;
  organization_id?: string | null;
  name: string;
  sku: string | null;
  barcode: string | null;
  price: number;
  is_active?: boolean;
  deleted_at?: string | null;
  item_type?: string | null;
  is_misc?: boolean | null;
  category_id?: string | null;
  brand?: string | null;
  brand_id?: string | null;
  preferred_supplier_id?: string | null;
  supplier_item_code?: string | null;
  unit_of_measure?: string | null;
  image_url?: string | null;
};

const PREFLIGHT_WAIT_MS = 4_000;
const CATALOG_SYNC_WAIT_TIMEOUT = Symbol('catalog-sync-wait-timeout');

/**
 * Espera acotada del pull de catálogo.
 * - Resuelve: devuelve el resultado del pull.
 * - Rechaza: PROPAGA el error (antes se tragaba → falso empty state).
 * - Timeout: devuelve sentinel y sigue con la cache que haya; el listener de
 *   catalogSynced re-carga la lista cuando el snapshot termine de llegar.
 */
const waitCatalogSyncWithTimeout = async <T>(
  promise: Promise<T>
): Promise<T | typeof CATALOG_SYNC_WAIT_TIMEOUT> => {
  const outcome = await Promise.race([
    promise.then(
      (value) => ({ ok: true as const, value }),
      (error: unknown) => ({ ok: false as const, error })
    ),
    new Promise<{ ok: 'timeout' }>((resolve) =>
      setTimeout(() => resolve({ ok: 'timeout' }), PREFLIGHT_WAIT_MS)
    ),
  ]);
  if (outcome.ok === 'timeout') {
    // Evita unhandled rejection cuando el pull termine (bien o mal) después.
    promise.catch(() => {});
    return CATALOG_SYNC_WAIT_TIMEOUT;
  }
  if (!outcome.ok) {
    throw outcome.error;
  }
  return outcome.value;
};

type StockSnapshotPreflightFn = () => Promise<unknown>;
let stockSnapshotPreflightForTests: StockSnapshotPreflightFn | null = null;

/** Seam de test (mismo patrón que `hasSession` en syncCatalogSnapshotFromRemote). */
export const __setStockSnapshotPreflightForTests = (
  fn: StockSnapshotPreflightFn | null
): void => {
  stockSnapshotPreflightForTests = fn;
};

const runStockSnapshotPreflight = (): Promise<unknown> =>
  stockSnapshotPreflightForTests
    ? stockSnapshotPreflightForTests()
    : preflightCatalogSnapshotForRead();

const fetchRemoteStockSnapshot = async (
  explicitSession?: Session | null
): Promise<{
  stocks: RemoteSnapshotStockRow[];
  productsById: Map<string, RemoteSnapshotProductRow>;
}> => {
  const session =
    explicitSession !== undefined ? explicitSession : useAuthStore.getState().session;
  if (!session) return { stocks: [], productsById: new Map() };

  const existing = getCatalogRamSnapshotCache();
  const cacheHasProducts = () => {
    const snap = getCatalogRamSnapshotCache();
    return snap != null && snap.products.length > 0;
  };

  // Un preflight en vuelo (nacido antes de un ingreso) pisa el parche local con un snapshot viejo.
  // La pantalla ya prefighta al entrar / al forzar refresh; acá leemos la cache con tope de espera.
  if (!existing || existing.products.length === 0) {
    try {
      const inFlight = getActiveCatalogSnapshotSync();
      if (inFlight) {
        await waitCatalogSyncWithTimeout(inFlight);
      }
      // Si el pull en vuelo no llenó la cache (p. ej. pull no-forzado que resolvió
      // applied:false tragando el error remoto), el preflight fuerza el pull
      // completo que sí propaga el error.
      if (!cacheHasProducts()) {
        const result = await waitCatalogSyncWithTimeout(runStockSnapshotPreflight());
        if (result !== CATALOG_SYNC_WAIT_TIMEOUT && !cacheHasProducts()) {
          const confirmedEmpty =
            (result as { catalogStillEmpty?: boolean } | null | undefined)
              ?.catalogStillEmpty === true;
          // El pull resolvió sin confirmar vacío genuino y la cache sigue vacía:
          // fallo encubierto (error tragado aguas arriba) → error, no empty.
          if (!confirmedEmpty) {
            throw new SyncPullFailedError(
              'catalog',
              'No se pudo actualizar catálogo e inventario.'
            );
          }
        }
      }
    } catch (e) {
      // Fallo remoto + cache vacía = error con Reintentar (nunca falso empty).
      // Solo se sirve cache silenciosa cuando hay datos reales.
      if (!cacheHasProducts()) {
        throw e;
      }
      console.warn('[stockQueries] preflight de catálogo falló; sirviendo snapshot en cache', e);
    }
  }

  const cached = getCatalogRamSnapshotCache();
  if (!cached) {
    return { stocks: [], productsById: new Map() };
  }

  const stocks = cached.stocks as RemoteSnapshotStockRow[];
  const products = cached.products as RemoteSnapshotProductRow[];
  const productsById = new Map(products.map((product) => [product.id, product]));
  return { stocks, productsById };
};

/** Resuelve label, sku, barcode e imagen de una variante desde la cache RAM de product_variants. */
const resolveVariantMeta = (
  variantId: string | null | undefined,
): { label: string | null; sku: string | null; barcode: string | null; imageUrl: string | null } => {
  if (!variantId) return { label: null, sku: null, barcode: null, imageUrl: null };
  const cached = getCatalogRamSnapshotCache()?.productVariants.find(
    (pv) => String(pv.id) === variantId,
  );
  if (!cached) return { label: null, sku: null, barcode: null, imageUrl: null };
  const suffix = String((cached as { name_suffix?: string | null }).name_suffix ?? '').trim();
  let label: string | null = suffix || null;
  if (!label) {
    const attrsRaw = (cached as { attributes_json?: string | null }).attributes_json;
    if (attrsRaw) {
      try {
        const attrs = JSON.parse(attrsRaw) as Record<string, string>;
        const built = Object.entries(attrs).map(([k, v]) => `${k}: ${v}`).join(' · ');
        if (built) label = built;
      } catch { /* ignore */ }
    }
  }
  const sku = (cached as { sku?: string | null }).sku ?? null;
  const barcode = (cached as { barcode?: string | null }).barcode ?? null;
  const imageUrl = (cached as { image_url?: string | null }).image_url ?? null;
  return { label: label ?? 'Variante', sku, barcode, imageUrl };
};

/**
 * Un producto entra al listado de stock solo si está vendible:
 * no misc, no servicio, no dado de baja (`is_active === false` o `deleted_at`).
 * Antes solo filtraba misc/servicio → productos eliminados aparecían como stock normal.
 */
const isStockListableProduct = (
  product: Pick<RemoteSnapshotProductRow, 'is_misc' | 'item_type' | 'is_active' | 'deleted_at'>
): boolean =>
  product.is_misc !== true &&
  product.item_type !== 'service' &&
  product.is_active !== false &&
  (product.deleted_at == null || product.deleted_at === '');

const UNSTOCKED_ROW_ID_PREFIX = 'unstocked-';

/** La fila del listado es sintética (producto de catálogo sin stock cargado) — no existe en inventory.stocks. */
export const isUnstockedStockRow = (row: Pick<StockRow, 'id'>): boolean =>
  row.id.startsWith(UNSTOCKED_ROW_ID_PREFIX);

/**
 * Ticket soporte: la lista de Stock es «catálogo ∪ movimientos» — un producto
 * sin fila en inventory.stocks aparece con qty 0 para que el comercio pueda
 * cargarlo. Antes la lista era solo filas de stock → los productos recién
 * cargados al catálogo eran invisibles hasta el primer ingreso
 * («cargué todo el catálogo y en Stock me aparece la mitad»).
 * Mismo merge que ya hace physicalCountCore (id sintético, stockId null).
 * El filtro 'zero' los captura (qty 0 = "sin cargar"); los demás filtros de
 * status los excluyen solos (qty 0 no es low/expired/expiring/no_sales).
 */
const synthesizeUnstockedProductRows = (
  branchStocks: RemoteSnapshotStockRow[],
  productsById: Map<string, RemoteSnapshotProductRow>,
  branchId: string
): StockRow[] => {
  const stockedProductIds = new Set(branchStocks.map((row) => row.product_id));
  const rows: StockRow[] = [];
  for (const product of productsById.values()) {
    if (stockedProductIds.has(product.id)) continue;
    if (!isStockListableProduct(product)) continue;
    rows.push({
      id: `${UNSTOCKED_ROW_ID_PREFIX}${branchId}-${product.id}`,
      productId: product.id,
      branchId,
      productName: product.name ?? '',
      productSku: product.sku ?? '',
      productBarcode: product.barcode ?? product.sku ?? null,
      productPrice: Number(product.price ?? 0),
      unitOfMeasure: normalizeStockUnitOfMeasure(product.unit_of_measure),
      quantity: 0,
      avgCost: 0,
      minQuantity: 0,
      expiresAt: null,
      productBrandId: product.brand_id ?? null,
      productPreferredSupplierId: product.preferred_supplier_id ?? null,
      productSupplierItemCode: product.supplier_item_code ?? null,
      productBrand: product.brand ?? null,
      variantId: null,
      variantLabel: null,
      variantSku: null,
      variantBarcode: null,
      imageUrl: product.image_url ?? null,
    } as StockRow);
  }
  return rows;
};

const buildVisibleStockRows = (
  stocks: RemoteSnapshotStockRow[],
  productsById: Map<string, RemoteSnapshotProductRow>,
  branchId: string,
  opts: GetStockByBranchOptions | undefined,
  options?: { includeVariants?: boolean }
): StockRow[] => {
  const branchStocks = stocks.filter((row) => row.branch_id === branchId);
  const real = mapRemoteRowsToStockRows(branchStocks, productsById, options);
  if (opts?.includeUnstocked === false) {
    return real.filter((row) => applyAllFilters(row, opts ?? {}, productsById));
  }
  return [
    ...real,
    ...synthesizeUnstockedProductRows(branchStocks, productsById, branchId),
  ].filter((row) => applyAllFilters(row, opts ?? {}, productsById));
};

const mapRemoteRowsToStockRows = (
  rows: RemoteSnapshotStockRow[],
  productsById: Map<string, RemoteSnapshotProductRow>,
  options?: { includeVariants?: boolean }
): StockRow[] => {
  // #800: Agregar por producto priorizando filas parent (variant_id null).
  // Antes se mapeaba cada fila (incluyendo variantes) → duplicados en stock list web.
  const includeVariants = options?.includeVariants === true;
  const byProduct = new Map<string, RemoteSnapshotStockRow>();
  const parentRows = rows.filter((r) => r.variant_id == null || r.variant_id === '');
  const hasParent = new Set(parentRows.map((r) => r.product_id));
  const rowsToAggregate = rows.filter((r) => {
    if (r.variant_id == null || r.variant_id === '') return true;
    return !hasParent.has(r.product_id);
  });
  for (const row of rowsToAggregate) {
    const existing = byProduct.get(row.product_id);
    if (!existing) {
      byProduct.set(row.product_id, { ...row, variant_id: null });
      continue;
    }
    const existingQty = Number(existing.quantity ?? 0);
    const rowQty = Number(row.quantity ?? 0);
    const nextQty = existingQty + rowQty;
    // #801: Fix avgCost — weighted average when summing variant-only products.
    const existingAvg = Number(existing.avg_cost ?? 0);
    const rowAvg = Number(row.avg_cost ?? 0);
    const nextAvg = nextQty > 0 ? (existingQty * existingAvg + rowQty * rowAvg) / nextQty : 0;
    // #801: Fix expiresAt — MIN across variants (null if both null).
    const existingExp = existing.expires_at ?? null;
    const rowExp = row.expires_at ?? null;
    let nextExp: string | null = existingExp;
    if (rowExp != null) {
      nextExp = existingExp == null ? rowExp : (existingExp < rowExp ? existingExp : rowExp);
    }
    byProduct.set(row.product_id, {
      ...existing,
      quantity: nextQty,
      avg_cost: nextAvg,
      min_quantity: Math.max(Number(existing.min_quantity ?? 0), Number(row.min_quantity ?? 0)),
      expires_at: nextExp,
      variant_id: null,
    });
  }

  const parentStockRows: StockRow[] = Array.from(byProduct.values())
    .map((row) => {
      const product = productsById.get(row.product_id);
      if (!product) return null;
      if (!isStockListableProduct(product)) return null;
      return {
        id: row.id,
        productId: row.product_id,
        branchId: row.branch_id,
        productName: product.name ?? '',
        productSku: product.sku ?? '',
        productBarcode: product.barcode ?? product.sku ?? null,
        productPrice: Number(product.price ?? 0),
        unitOfMeasure: normalizeStockUnitOfMeasure(product.unit_of_measure),
        quantity: Number(row.quantity ?? 0),
        avgCost: Number(row.avg_cost ?? 0),
        minQuantity: Number(row.min_quantity ?? 0),
        expiresAt: row.expires_at ?? null,
        productBrandId: product.brand_id ?? null,
        productPreferredSupplierId: product.preferred_supplier_id ?? null,
        productSupplierItemCode: product.supplier_item_code ?? null,
        productBrand: product.brand ?? null,
        variantId: null,
        variantLabel: null,
        variantSku: null,
        variantBarcode: null,
        imageUrl: product.image_url ?? null,
      } as StockRow;
    })
    .filter((row): row is StockRow => !!row);

  if (!includeVariants) return parentStockRows;

  // #801: Build variant rows from remote snapshot rows that have variant_id populated.
  // Only include variants for products that pass the parent filter (physical, non-misc).
  const validProductIds = new Set(parentStockRows.map((r) => r.productId));
  const variantRows: StockRow[] = rows
    .filter((r) => r.variant_id != null && r.variant_id !== '' && validProductIds.has(r.product_id))
    .map((row) => {
      const product = productsById.get(row.product_id);
      if (!product) return null;
      const meta = resolveVariantMeta(row.variant_id);
      return {
        id: row.id,
        productId: row.product_id,
        branchId: row.branch_id,
        productName: product.name ?? '',
        productSku: product.sku ?? '',
        productBarcode: product.barcode ?? product.sku ?? null,
        productPrice: Number(product.price ?? 0),
        unitOfMeasure: normalizeStockUnitOfMeasure(product.unit_of_measure),
        quantity: Number(row.quantity ?? 0),
        avgCost: Number(row.avg_cost ?? 0),
        minQuantity: Number(row.min_quantity ?? 0),
        expiresAt: row.expires_at ?? null,
        productBrandId: product.brand_id ?? null,
        productPreferredSupplierId: product.preferred_supplier_id ?? null,
        productSupplierItemCode: product.supplier_item_code ?? null,
        productBrand: product.brand ?? null,
        variantId: row.variant_id,
        variantLabel: meta.label,
        variantSku: meta.sku,
        variantBarcode: meta.barcode,
        // Thumb de fila: la imagen de la variante si tiene, si no la del producto.
        imageUrl: meta.imageUrl ?? product.image_url ?? null,
      } as StockRow;
    })
    .filter((row): row is StockRow => !!row);

  return [...parentStockRows, ...variantRows];
};

const applyRemoteCategoryFilter = (
  row: StockRow,
  productsById: Map<string, RemoteSnapshotProductRow>,
  category: string | 'all' | undefined
) => {
  if (!category || category === 'all') return true;
  const p = productsById.get(row.productId);
  if (!p) return false;
  return productMatchesCategoryFilter(p.category_id ?? null, category);
};

const applyRemoteBrandSupplierFilter = (
  row: StockRow,
  productsById: Map<string, RemoteSnapshotProductRow>,
  brandId?: string | null,
  supplierId?: string | null
) => {
  const brandIdVal =
    brandId != null && String(brandId).trim() !== '' ? String(brandId).trim() : '';
  const supplierTrim =
    supplierId != null && String(supplierId).trim() !== '' ? String(supplierId).trim() : '';
  const p = productsById.get(row.productId);
  if (!p) return false;
  if (brandIdVal) {
    const b = p.brand_id != null ? String(p.brand_id).trim() : '';
    if (b !== brandIdVal) return false;
  }
  if (supplierTrim) {
    const sid = p.preferred_supplier_id != null ? String(p.preferred_supplier_id).trim() : '';
    if (sid !== supplierTrim) return false;
  }
  return true;
};

/**
 * Listado paginado de stock por sucursal.
 * Lee siempre SQLite local (misma fuente que countStockByBranch y el dashboard).
 * La frescura remota la garantiza `preflightCatalogSnapshotForRead` antes de cargar la pantalla.
 */
export const getStockByBranch = async (
  branchId: string,
  opts?: GetStockByBranchOptions
): Promise<StockRow[]> => {
  const limit = opts?.limit;
  const offset = opts?.offset ?? 0;

    const { stocks, productsById } = await fetchRemoteStockSnapshot(opts?.authSessionForRemoteFallback);
        const orgId = [...productsById.values()]
          .map((p) => (p.organization_id != null ? String(p.organization_id).trim() : ''))
          .find((id) => id.length > 0);
        const comboParents =
          orgId != null && orgId.length > 0
            ? await getComboParentProductIds(orgId, [...productsById.keys()])
            : new Set<string>();
        const rows = buildVisibleStockRows(stocks, productsById, branchId, opts, {
          includeVariants: opts?.includeVariants,
        })
          .filter((row) => !comboParents.has(row.productId))
          .sort(stockRowComparator(opts?.sort));
        return typeof limit === 'number' ? rows.slice(offset, offset + limit) : rows.slice(offset);
};

export const countStockByBranch = async (
  branchId: string,
  opts?: Pick<
    GetStockByBranchOptions,
    | 'search'
    | 'statusFilter'
    | 'categoryId'
    | 'pinnedProductId'
    | 'brandId'
    | 'supplierId'
    | 'productIdAllowlist'
    | 'authSessionForRemoteFallback'
  >
): Promise<number> => {
    const { stocks, productsById } = await fetchRemoteStockSnapshot(opts?.authSessionForRemoteFallback);
        const orgId = [...productsById.values()]
          .map((p) => (p.organization_id != null ? String(p.organization_id).trim() : ''))
          .find((id) => id.length > 0);
        const comboParents =
          orgId != null && orgId.length > 0
            ? await getComboParentProductIds(orgId, [...productsById.keys()])
            : new Set<string>();
        return buildVisibleStockRows(stocks, productsById, branchId, opts).filter(
          (row) => !comboParents.has(row.productId),
        ).length;
};

export const getStockByProductAndBranch = async (
  branchId: string,
  productId: string,
  opts?: { authSessionForRemoteFallback?: Session | null }
): Promise<ProductBranchStockRow | null> => {
  const resolveFromRemoteSnapshot = async (): Promise<ProductBranchStockRow | null> => {
    const { stocks, productsById } = await fetchRemoteStockSnapshot(opts?.authSessionForRemoteFallback);
    const product = productsById.get(productId);
    if (!product || !isStockListableProduct(product)) return null;
    const row = stocks.find(
      (stock) =>
        stock.branch_id === branchId &&
        stock.product_id === productId &&
        (stock.variant_id == null || stock.variant_id === ''),
    );
    if (!row) return null;
    return {
      id: row.id,
      productId: row.product_id,
      branchId: row.branch_id,
      quantity: Number(row.quantity ?? 0),
      avgCost: Number(row.avg_cost ?? 0),
      minQuantity: Number(row.min_quantity ?? 0),
      expiresAt: row.expires_at ?? null,
    };
  };

    return resolveFromRemoteSnapshot();
};

export const getInventoryDashboard = async (
  branchId: string,
  opts?: { authSessionForRemoteFallback?: Session | null }
): Promise<InventoryDashboardRow> => {
  let rows: Array<{ quantity: number; min_quantity: number; expires_at: string | null; product_id: string }> = [];
  let totalValue = 0;

  const loadRemoteDashboardRows = async () => {
    const snapshot = await fetchRemoteStockSnapshot(opts?.authSessionForRemoteFallback);
    const orgId = [...snapshot.productsById.values()]
      .map((p) => (p.organization_id != null ? String(p.organization_id).trim() : ''))
      .find((id) => id.length > 0);
    const comboParents =
      orgId != null && orgId.length > 0
        ? await getComboParentProductIds(orgId, [...snapshot.productsById.keys()])
        : new Set<string>();
    // #801: Agregar por producto priorizando parent, sumando variantes si no hay parent.
    // Antes filtraba variant_id IS NULL → productos con stock solo en variantes mostraban 0.
    const aggregated = aggregateRemoteStocksByProduct(branchId, snapshot.stocks, { weightedAvgCost: true });
    rows = Array.from(aggregated.values())
      .filter((row) => {
        const product = snapshot.productsById.get(row.product_id);
        return (
          product != null &&
          isStockListableProduct(product) &&
          !comboParents.has(row.product_id)
        );
      })
      .map((row) => ({
        quantity: Number(row.quantity ?? 0),
        min_quantity: Number(row.min_quantity ?? 0),
        expires_at: (row as RemoteSnapshotStockRow).expires_at ?? null,
        product_id: row.product_id,
      }));
    totalValue = Array.from(aggregated.values()).reduce((acc, row) => {
      const product = snapshot.productsById.get(row.product_id);
      if (
        product == null ||
        !isStockListableProduct(product) ||
        comboParents.has(row.product_id)
      ) {
        return acc;
      }
      return acc + Number(row.quantity ?? 0) * Number(row.avg_cost ?? 0);
    }, 0);
  };

    await loadRemoteDashboardRows();

  const now = Date.now();
  let belowMin = 0;
  let expiringSoon = 0;
  let negative = 0;
  let expired = 0;
  let withMin = 0;
  let withCoverage = 0;

  for (const r of rows) {
    const expTime = r.expires_at ? new Date(r.expires_at).getTime() : null;
    // #1497: lotes como fuente de verdad del vencimiento (expires_at puede
    // venir stale hasta el próximo refresh del servidor). Las filas están
    // agregadas por producto en la sucursal actual — split a nivel producto.
    const lotsExpired =
      (getStockLotExpirySplit(r.product_id, branchId)?.expiredQuantity ?? 0) > 0;
    const isExpired = (expTime != null && expTime < now) || lotsExpired;
    const isExpiring =
      expTime != null && expTime >= now && expTime - now < SEVEN_DAYS_MS;

    // «Negativo» usa el mismo criterio que el filtro 'negative' del listado:
    // qty < 0 y NO vencido (los vencidos cuentan en `expired`, tratamiento aparte).
    // Buckets disjuntos (KPI == filtro): expired > negative > expiring > low.
    // «Por vencer» no exige qty>min: por vencer + bajo mínimo es urgencia
    // de vencimiento, no de reposición (antes quedaba invisible al alerta).
    if (!isExpired && r.quantity < 0) negative += 1;
    else if (!isExpired && !isExpiring && r.quantity > 0 && r.quantity <= r.min_quantity) belowMin += 1;

    if (isExpired) expired += 1;
    else if (isExpiring && r.quantity > 0) expiringSoon += 1;
    // Cobertura = % de productos CON mínimo definido que están en o por encima del mínimo.
    // Antes el denominador era todos los productos → 0% engañoso si pocos tienen min.
    if (r.min_quantity > 0) {
      withMin += 1;
      if (r.quantity >= r.min_quantity) withCoverage += 1;
    }
  }

  const coveragePercent =
    withMin > 0
      ? Math.round((withCoverage / withMin) * 100)
      : rows.length > 0
        ? 100
        : 0;
  return {
    totalProducts: rows.length,
    belowMin,
    expiringSoon,
    negative,
    expired,
    coveragePercent,
    totalValue,
  };
};

export const getLastStockMovementUnitCost = async (
  productId: string,
  branchId: string
): Promise<number | null> => {
  // #867: web ram-only no tiene stock_movements en cache.
        // Fallback: usar avg_cost del stock row del catálogo RAM.
        // No es el último costo de recepción exacto, pero es una sugerencia
        // razonable para "correct cost" en web.
        const cached = getCatalogRamSnapshotCache();
        if (!cached) return null;
        const stock = (cached.stocks as RemoteSnapshotStockRow[]).find(
          (s) =>
            s.product_id === productId &&
            s.branch_id === branchId &&
            (s.variant_id == null || s.variant_id === ''),
        );
        return stock?.avg_cost != null ? Number(stock.avg_cost) : null;
};

export type VariantStockCellRow = {
  variantId: string;
  stockId: string | null;
  label: string;
  quantity: number;
  /** Imagen propia de la variante (product_variants.image_url). */
  imageUrl?: string | null;
};

export const listVariantStockCells = async (
  branchId: string,
  productId: string,
): Promise<VariantStockCellRow[]> => {
  const cache = getCatalogRamSnapshotCache();
        if (!cache) return [];
        const variantStocks = (cache.stocks as RemoteSnapshotStockRow[]).filter(
          (s) =>
            s.product_id === productId &&
            s.branch_id === branchId &&
            (s as { variant_id?: string | null }).variant_id,
        );
        const variantsById = new Map(
          cache.productVariants.map((pv) => [String(pv.id), pv]),
        );
        return variantStocks
          .map((s) => {
            const variantId = String((s as { variant_id?: string | null }).variant_id ?? '');
            const pv = variantsById.get(variantId);
            const suffix = String((pv as { name_suffix?: string | null })?.name_suffix ?? '').trim();
            let label = suffix;
            if (!label) {
              const attrsJson = (pv as { attributes_json?: string | null })?.attributes_json;
              if (attrsJson) {
                try {
                  const attrs = JSON.parse(attrsJson) as Record<string, string>;
                  label = Object.entries(attrs).map(([k, v]) => `${k}: ${v}`).join(' · ');
                } catch {
                  label = '';
                }
              }
            }
            return {
              variantId,
              // #737: no generar UUID random si falta id — marcar como null
              // para que el caller sepa que no hay stock row real
              stockId: s.id ? String(s.id) : null,
              label: label || 'Variante',
              quantity: Number(s.quantity ?? 0),
              imageUrl: (pv as { image_url?: string | null } | undefined)?.image_url ?? null,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label));
};
